import { useCallback, useEffect, useRef, useState } from 'react';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      size?: 'normal' | 'compact' | 'flexible';
    },
  ) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile failed to load'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Explicit-render Turnstile. When `siteKey` is null (no secret configured on the
 * server) this does nothing at all and reports itself ready — that is the
 * documented local-development path.
 */
export function useTurnstile(siteKey: string | null): {
  widgetRef: React.RefObject<HTMLDivElement | null>;
  ready: boolean;
  token: string | null;
  reset: () => void;
} {
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [ready, setReady] = useState(siteKey === null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!siteKey) {
      setReady(true);
      return;
    }
    let cancelled = false;

    void loadScript()
      .then(() => {
        if (cancelled || !widgetRef.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          theme: 'auto',
          size: 'flexible',
          callback: (value) => {
            setToken(value);
            setReady(true);
          },
          'error-callback': () => setReady(true),
          'expired-callback': () => setToken(null),
        });
      })
      .catch(() => setReady(true));

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [siteKey]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
  }, []);

  return { widgetRef, ready, token, reset };
}
